// ============================================================================
// BOQ — reusable click-to-sort for tables (mirrors the timetracker pattern)
// ============================================================================
// Pass rows and an accessors map { field: (row) => value }. Clicking a header
// (via toggle) sorts asc, clicking again flips to desc. Strings sort with
// natural/numeric collation; nulls sort last. firstDir sets the direction of
// the first click per field ({ modified: 'desc' } puts newest first); fields
// not named there start ascending.
// ============================================================================

import { useCallback, useMemo, useRef, useState } from 'react';

export function useSort(rows, accessors, initial = { field: null, dir: 'asc' }, firstDir = {}) {
  const accRef = useRef(accessors);
  accRef.current = accessors;
  const firstDirRef = useRef(firstDir);
  firstDirRef.current = firstDir;
  const [sort, setSort] = useState(initial);

  const toggle = useCallback((field) => {
    setSort((s) => (s.field === field
      ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { field, dir: firstDirRef.current[field] || 'asc' }));
  }, []);

  const sorted = useMemo(() => {
    const acc = sort.field ? accRef.current[sort.field] : null;
    if (!acc) return rows;
    return [...rows].sort((a, b) => {
      const va = acc(a);
      const vb = acc(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'string' || typeof vb === 'string') {
        const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
        return sort.dir === 'asc' ? cmp : -cmp;
      }
      return sort.dir === 'asc' ? va - vb : vb - va;
    });
  }, [rows, sort]);

  return { sorted, sort, toggle };
}
